import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { URLParamKey } from "constants/urlParams";
import { HMO } from "constants/hmo";
import { getParamCaseInsensitive } from "utils/urlParams.util";
import { detectHmo } from "utils/hmoDetection";
import {
  getLeumitToken,
  hasValidatedPersistedLeumitToken,
  markLeumitTokenValidated,
} from "services/leumitSession";
import {
  validateLeumitToken,
  type LeumitTokenValidationOutcome,
} from "services/leumitTokenValidation";

export type LeumitTokenValidationStatus =
  | "idle"
  | "validating"
  | "valid"
  | "invalid"
  | "error";

const getUrlToken = (search: string): string | null => {
  const queryParams = new URLSearchParams(search);
  const urlToken = getParamCaseInsensitive(queryParams, URLParamKey.TOKEN);
  return urlToken?.trim() || null;
};

const requiresLeumitToken = (): boolean => {
  if (typeof window === "undefined") {
    return false;
  }

  const isReactNativeWebView = (window as any).ReactNativeWebView !== undefined;
  const isInIframe = window.parent !== window;

  return isReactNativeWebView || isInIframe;
};

const resolveTokenForValidation = (search: string): string | null => {
  const urlToken = getUrlToken(search);

  if (urlToken) {
    return urlToken;
  }

  if (requiresLeumitToken()) {
    return getLeumitToken()?.trim() || null;
  }

  return null;
};

const shouldSkipValidation = (token: string): boolean => {
  return hasValidatedPersistedLeumitToken(token);
};

const resolveInitialStatus = (
  isLeumitTenant: boolean,
  search: string,
): LeumitTokenValidationStatus => {
  if (!isLeumitTenant) {
    return "valid";
  }

  const token = resolveTokenForValidation(search);

  if (!token && !requiresLeumitToken()) {
    return "valid";
  }

  if (token && shouldSkipValidation(token)) {
    return "valid";
  }

  return "idle";
};

const useLeumitTokenValidation = (): {
  status: LeumitTokenValidationStatus;
  retry: () => void;
} => {
  const { search } = useLocation();
  const isLeumitTenant = detectHmo() === HMO.LEUMIT;
  const [status, setStatus] = useState<LeumitTokenValidationStatus>(() =>
    resolveInitialStatus(isLeumitTenant, search),
  );
  const [retryNonce, setRetryNonce] = useState(0);
  const inFlightTokenRef = useRef<string | null>(null);
  const hasEverValidatedRef = useRef(false);

  useEffect(() => {
    if (!isLeumitTenant) {
      setStatus("valid");
      return;
    }

    if (hasEverValidatedRef.current) {
      setStatus("valid");
      return;
    }

    let cancelled = false;

    const applyValidationOutcome = (
      outcome: LeumitTokenValidationOutcome,
      token?: string,
    ): void => {
      if (cancelled) {
        return;
      }

      inFlightTokenRef.current = null;

      if (outcome === "valid") {
        if (token) {
          markLeumitTokenValidated(token);
        }
        hasEverValidatedRef.current = true;
        setStatus("valid");
        return;
      }

      if (outcome === "invalid") {
        setStatus("invalid");
        return;
      }

      setStatus("error");
    };

    const runValidation = async (): Promise<void> => {
      const token = resolveTokenForValidation(search);

      if (!token) {
        if (!cancelled) {
          inFlightTokenRef.current = null;
          setStatus(requiresLeumitToken() ? "invalid" : "valid");
        }
        return;
      }

      if (shouldSkipValidation(token)) {
        applyValidationOutcome("valid", token);
        return;
      }

      if (inFlightTokenRef.current === token) {
        return;
      }

      inFlightTokenRef.current = token;

      if (!cancelled) {
        setStatus("validating");
      }

      const outcome = await validateLeumitToken(token);

      if (cancelled) {
        return;
      }

      applyValidationOutcome(outcome, outcome === "valid" ? token : undefined);
    };

    void runValidation();

    return () => {
      cancelled = true;
      inFlightTokenRef.current = null;
    };
  }, [isLeumitTenant, search, retryNonce]);

  const handleRetry = useCallback((): void => {
    inFlightTokenRef.current = null;
    setRetryNonce((current) => current + 1);
  }, []);

  return {
    status,
    retry: handleRetry,
  };
};

export default useLeumitTokenValidation;
